import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, ExternalLink, Code2, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function WidgetPreview() {
  const { t } = useTranslation();
  const [slug, setSlug] = useState(null);
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    setOrigin(window.location.origin);
    api.get("/settings").then((r) => setSlug(r.data.business_slug)).catch(() => {});
  }, []);

  const bookUrl = slug ? `${origin}/book/${slug}` : "";

  const iframeCode = `<iframe
  src="${bookUrl}"
  width="100%"
  height="700"
  frameborder="0"
  style="border: none; border-radius: 12px; max-width: 480px;">
</iframe>`;

  const scriptCode = `<div id="probook-widget"></div>
<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${bookUrl}';
  iframe.style.width = '100%';
  iframe.style.maxWidth = '480px';
  iframe.style.height = '700px';
  iframe.style.border = 'none';
  iframe.style.borderRadius = '12px';
  document.getElementById('probook-widget').appendChild(iframe);
})();
</script>`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("widget_preview_copied"));
  };

  return (
    <div data-testid="widget-preview-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("widget_preview_title")}</h1>
        <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => bookUrl && window.open(bookUrl, "_blank")} disabled={!bookUrl} data-testid="open-widget-button">
          <ExternalLink size={14} className="mr-2" /> {t("widget_preview_open")}
        </Button>
      </div>

      {!slug && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" /> {t("widget_preview_loading")}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Embed Code */}
        <div className="space-y-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-white font-heading flex items-center gap-2">
                <Code2 size={16} className="text-indigo-400" /> {t("widget_preview_embed")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="iframe">
                <TabsList className="bg-slate-800 border-slate-700">
                  <TabsTrigger value="iframe" className="text-xs data-[state=active]:bg-slate-700">iframe</TabsTrigger>
                  <TabsTrigger value="script" className="text-xs data-[state=active]:bg-slate-700">Script</TabsTrigger>
                </TabsList>
                <TabsContent value="iframe" className="mt-3">
                  <div className="relative">
                    <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto font-mono whitespace-pre-wrap" data-testid="iframe-code">
                      {iframeCode}
                    </pre>
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 text-slate-400 hover:text-white" onClick={() => copy(iframeCode)} data-testid="copy-iframe-code">
                      <Copy size={14} />
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="script" className="mt-3">
                  <div className="relative">
                    <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto font-mono whitespace-pre-wrap" data-testid="script-code">
                      {scriptCode}
                    </pre>
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 text-slate-400 hover:text-white" onClick={() => copy(scriptCode)} data-testid="copy-script-code">
                      <Copy size={14} />
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
              <p className="text-xs text-slate-500 mt-3">
                {t("widget_preview_embed_hint")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white font-heading">{t("widget_preview_live")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-950 rounded-lg border border-slate-800 overflow-hidden" style={{ height: 600 }}>
              {bookUrl
                ? <iframe src={bookUrl} title="Booking Widget Preview" className="w-full h-full border-0" data-testid="widget-preview-iframe" />
                : <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-slate-600" size={24} /></div>
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
